import { AgentProfile, AppPresence } from "@closerplatform/spinner-openapi";
import {
  ChatComponent,
  ChatComponentRef
} from "@fidget/common/components/chat/chatComponent/chatComponent";
import { LoaderComponent } from "@fidget/common/components/loader/loader";
import { NotificationComponent } from "@fidget/common/components/notificationComponent";
import { PoweredBy } from "@fidget/common/components/poweredBy";
import { staticConfig } from "@fidget/common/config/static-config";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { ChatItemGroup } from "@fidget/common/models/chat/chatItemGroup";
import {
  DynamicSuggestionsChatItem,
  IAttachment,
  IChatItem,
  TypingChatItem
} from "@fidget/common/models/chat/chatItems";
import { SafeSessionStorageService } from "@fidget/common/models/safeSessionStorageService";
import { isAgentProfile } from "@fidget/common/models/userProfile";
import { SafeLocalStorageService } from "@fidget/common/services/safeLocalStorageService";
import { logger } from "@fidget/common/utils";
import { isOrangeOrg } from "@fidget/common/utils/isOrange";
import { getCurrentMaintenanceState } from "@fidget/common/utils/maintenance";
import { notEmpty } from "@fidget/common/utils/not-empty";
import classNames from "classnames";
import { throttle } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { userGetter } from "../../actions/common";
import { envActions } from "../../actions/env";
import { userActions } from "../../actions/user";
import { CallingHeader } from "../../components/call/callingHeader";
import { ControlsHeader } from "../../components/call/controlsHeader";
import { renderItem } from "../../components/chat/chatItemHelper";
import { ChatInputComponent } from "../../components/chatInputComponent";
import { MainNotification } from "../../components/feedback/mainNotification/mainNotification";
import { MeetingSlotPicker } from "../../components/meetingScheduler/meetingSlotPicker";
import { WidgetError } from "../../components/widgetError";
import { WidgetLoader } from "../../components/WidgetLoader";
import { WidgetMaintenance } from "../../components/widgetMaintenance";
import { ForeContext } from "../../context";
import { useStore } from "../../models";
import styles from "./mainContainer.css";
import { config } from "@fidget/common/config/config";
import { fileService } from "@fidget/common/services/files/fileService";
import { getWheelhouseDownloadUrl } from "@fidget/common/utils/urlMappingHelpers";

const userTypingCallback = throttle(
  (ctx: ForeContext, preview?: string) => userActions.userTyping(ctx, preview),
  config.services.times.chatTypingThrottleMillis
);

export const MainContainer = observer(() => {
  const {
    foreCallStore,
    context,
    uiStore,
    state,
    shortOrgConfig,
    slotsProvider,
    shouldShowPoweredBy,
    smallCallingHeader,
    chatComponent,
    breakingPoints,
    currentPage,
    isLoadingFirstTime,
    agents,
    usersTyping,
    dynamicSuggestions,
    onMeetingSlotSelected,
    onChatScrolledToTop,
    scrollChatToBottom,
    onCloseClicked,
    sendMessage,
    deinitWidget,
    phoneRegion,
    maintenanceEnabled,
    maintenanceMessage
  } = useMainContainerState();

  const isWidgetMaintenance = maintenanceEnabled || config.services.maintenance;
  const widgetMaintenanceMessage = maintenanceEnabled
    ? maintenanceMessage
    : config.services.maintenance
    ? config.services.maintenanceMessage
    : undefined;

  const typingAgents = usersTyping.filter((user) => isAgentProfile(user));

  const renderChatScrollButton = () => {
    return (
      <div
        className={classNames(styles["scroll-button"], styles["scroll-to-bottom"])}
        onClick={() => chatComponent.current && chatComponent.current.scrollToBottom()}
      >
        <i className={"icon-scroll-back-down"} />
      </div>
    );
  };

  const renderChatLoader = () => {
    return (
      <div
        className={classNames(styles.loader, {
          [styles["loader-visible"]]: state.room.isLoading && !isLoadingFirstTime
        })}
      >
        <LoaderComponent widgetStyle />
      </div>
    );
  };

  const renderChatChildren = () => {
    return typingAgents.length > 0
      ? renderItem(
          new ChatItemGroup(new TypingChatItem("typing", typingAgents[0] as AgentProfile, 0)),
          state.profile!,
          phoneRegion,
          undefined,
          () => chatComponent.current && chatComponent.current.scrollToBottom(),
          breakingPoints.isMobile
        )
      : false;
  };

  const renderDynamicSuggestion = () => {
    return dynamicSuggestions.length > 0
      ? renderItem(
          new DynamicSuggestionsChatItem("dynamicSuggestions", dynamicSuggestions, 0),
          state.profile!,
          phoneRegion,
          undefined,
          () => chatComponent.current && chatComponent.current.scrollToBottom(),
          breakingPoints.isMobile
        )
      : false;
  };

  const renderChatItem = (item: IChatItem) => {
    return renderItem(
      item,
      state.profile!,
      phoneRegion,
      (contact) => userActions.contactProvided(context, contact),
      () => chatComponent.current && chatComponent.current.scrollToBottom(),
      breakingPoints.isMobile
    );
  };

  const renderChatNotification = () => {
    if (uiStore.isWidgetInitializing) {
      return;
    }
    if (uiStore.chatNotification) {
      return (
        <NotificationComponent
          notification={uiStore.chatNotification}
          onDismiss={() => uiStore.removeChatNotification()}
        />
      );
    }

    if (uiStore.mainNotification) {
      return <MainNotification text={uiStore.mainNotification} />;
    }

    return;
  };

  return (
    <>
      {foreCallStore.isInProgress &&
        foreCallStore.videoExpanded &&
        !breakingPoints.shouldCallDrawerBeHidden && (
          <div
            className={classNames(styles["call-drawer"], {
              [styles.isMobile]: breakingPoints.isMobile
            })}
          >
            <CallingHeader context={context} breakingPoints={breakingPoints} />
          </div>
        )}
      <div
        className={classNames(styles["main-wrapper"], {
          [styles.isMobile]: breakingPoints.isMobile
        })}
      >
        {shortOrgConfig && (
          <ControlsHeader
            agents={agents}
            showCloseButton={state.sideBarMode}
            breakingPoints={breakingPoints}
            forgetMeAction={() => envActions.forgetMe(context)}
          />
        )}
        {(uiStore.isWidgetInitializing || (state.room.isLoading && isLoadingFirstTime)) && (
          <WidgetLoader />
        )}
        {isWidgetMaintenance && <WidgetMaintenance message={widgetMaintenanceMessage} />}
        {!isWidgetMaintenance && uiStore.initializationErrorMessageId && (
          <WidgetError
            messageId={uiStore.initializationErrorMessageId}
            onButtonClick={deinitWidget}
          />
        )}
        {!isWidgetMaintenance && !uiStore.isWidgetInitializing && (
          <>
            <ChatComponent
              containerClassName={styles.chat}
              ref={(el) => el && (chatComponent.current = el)}
              items={state.room.chat.history}
              renderItem={renderChatItem}
              onScrolledToTop={() => !state.room.historyIsFullyLoaded && onChatScrolledToTop()}
              onIsScrolledToBottom={(isBottom) =>
                userActions.chatIsScrolledToBottom(context, isBottom)
              }
              isScrolledToBottom={state.chatScrolledToBottom}
              loader={renderChatLoader()}
              scrollButton={renderChatScrollButton()}
              typingUsersComponent={renderChatChildren()}
              dynamicSuggestions={renderDynamicSuggestion()}
              isTyping={typingAgents.length > 0}
              notification={renderChatNotification()}
            />
            {shouldShowPoweredBy && false && (
              <PoweredBy currentPage={currentPage && currentPage.url} />
            )}
            {!!state.pendingMeetingToken && !!state.profile && (
              <MeetingSlotPicker
                token={state.pendingMeetingToken}
                slotsProvider={slotsProvider}
                onSlotPicked={onMeetingSlotSelected}
                onCancel={(token: string) => userActions.cancelMeetingScheduling(context, token)}
              />
            )}
            {!isLoadingFirstTime &&
              !uiStore.initializationErrorMessageId &&
              !state.pendingMeetingToken &&
              state.profile && (
                <ChatInputComponent
                  onMessageSent={(msg, attachments) => {
                    userTypingCallback.cancel();
                    sendMessage(msg, attachments);
                    scrollChatToBottom();
                  }}
                  onTyping={(preview) => userTypingCallback(context, preview)}
                  isTypingPreviewEnabled={shortOrgConfig?.typingPreviewEnabled || false}
                />
              )}
          </>
        )}
        {!((foreCallStore.isConnecting || foreCallStore.isInProgress) && smallCallingHeader) && (
          <div className={styles["mobile-close-button-container"]}>
            <button onClick={onCloseClicked}>
              <i className={"icon-close-cross"} />
            </button>
          </div>
        )}
      </div>
    </>
  );
});

const useMainContainerState = () => {
  const { foreCallStore, contextStore, uiStore } = useStore();
  const { foreContext: context } = contextStore;
  const isAutoCallEnabled = context.autoClientCalling;
  const localStorageKeyMessageSent =
    staticConfig.localStorageKeyPrefix + ":" + context.orgId + ":wasAutoCallingFirstMessageSent";
  const localStorageKeyTriedToAutoCall =
    staticConfig.localStorageKeyPrefix + ":" + context.orgId + ":triedToCallByAutoCalling";

  const [smallCallingHeader, setSmallCallingHeader] = useState<boolean>(window.innerHeight <= 400);
  const [shouldSendAutoCallingInitMessage, setShouldSendAutoCallingInitMessage] =
    useState<boolean>(isAutoCallEnabled);
  const chatComponent = useRef<ChatComponentRef>();

  const safeLocalStorage = SafeLocalStorageService.getInstance();
  const safeSessionStorage = SafeSessionStorageService.getInstance();
  const { state, spinnerService } = context;
  const isLoadingFirstTime = state.room.isLoading && !state.room.chat.history.length;
  const { breakingPoints, currentPage, shared } = state;
  const { orgConfig: shortOrgConfig } = shared;
  const shouldShowPoweredBy = !isOrangeOrg(context.orgId);
  const phoneRegion = shortOrgConfig?.phoneRegion || "";

  const agents = Object.keys(shared.agents)
    .map((key) => shared.agents.get(key))
    .filter(notEmpty)
    .filter((agent) => !agent.isBot);

  const usersTyping = state.room.usersTyping
    .map((uid) => userGetter(context)(uid, state.profile!.roomId))
    .filter(notEmpty);

  const dynamicSuggestions = state.room.chat.dynamicSuggestions;

  const maintenanceWindows = shortOrgConfig?.maintenanceWindows || [];
  const { message: maintenanceMessage, enabled: maintenanceEnabled } =
    getCurrentMaintenanceState(maintenanceWindows);

  const onResize = () => {
    setSmallCallingHeader(window.innerHeight <= 400);
  };

  const setFocus = () => {
    envActions.setWidgetActive(context, true);
  };

  const setBlur = () => {
    envActions.setWidgetActive(context, false);
  };

  const sendMessage = (text: string, attachments?: ArrayLike<IAttachment>) => {
    userActions.sendButtonClicked(context, text, attachments);
  };

  const deinitWidget = () => {
    logger.info("Fore deinit executed");
    context.hostWindowRequests.sendDeinitCommand();
  };

  const onMeetingSlotSelected = (token: string, start: number, duration: number) => {
    return userActions.scheduleMeeting(context, token, start, duration);
  };

  const onChatScrolledToTop = () => {
    const roomId = context.state.profile!.roomId;
    userActions.chatTopReached(context, roomId);
  };

  const scrollChatToBottom = () => {
    if (chatComponent.current) {
      chatComponent.current.scrollToBottom();
    }
  };

  const sendAutoCallingInitMessage = () => {
    const didSent = safeLocalStorage.getItem(localStorageKeyMessageSent) === "true";
    if (shouldSendAutoCallingInitMessage && !didSent) {
      setTimeout(() => sendMessage(intl.formatMessage("message.autocalling")), 1500);
      safeLocalStorage.setItem(localStorageKeyMessageSent, "true");
      setShouldSendAutoCallingInitMessage(false);
      setTimeout(
        () => sendAutoCallingFailedMessage(),
        config.services.times.autoCallingFailedTimeout
      );
    }
  };

  const sendAutoCallingFailedMessage = () => {
    const assignedAgent = context.state.shared.assignedAgent;
    if (!assignedAgent || assignedAgent.presence !== AppPresence.Available) {
      sendMessage(intl.formatMessage("message.autocalling.failed"));
      safeSessionStorage.setItem(localStorageKeyTriedToAutoCall, "true");
    }
  };

  const onCloseClicked = () => userActions.closeButtonClicked(context);

  useEffect(() => {
    window.addEventListener("focus", setFocus);
    window.addEventListener("blur", setBlur);
    window.addEventListener("resize", onResize);
    context.hostWindowRequests.onOpenWidgetWithAutoCalling(() => {
      sendAutoCallingInitMessage();
      return Promise.resolve();
    });

    return () => {
      window.removeEventListener("focus", setFocus);
      window.removeEventListener("blur", setBlur);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    (async () => {
      const agentAvatarUrl =
        context.state.shared.assignedAgent?.avatarToken &&
        getWheelhouseDownloadUrl(context.state.shared.assignedAgent?.avatarToken);
      const orgAvatarUrl =
        context.state.shared.orgConfig?.defaultAgentAvatarToken &&
        getWheelhouseDownloadUrl(context.state.shared.orgConfig?.defaultAgentAvatarToken);
      const url = agentAvatarUrl || orgAvatarUrl;

      if (url) {
        try {
          const blobUrl = await fileService.getDataUrl(url);
          const blob = await fetch(blobUrl).then((res) => res.blob());
          const reader = new FileReader();

          await new Promise((resolve, reject) => {
            reader.onload = resolve;
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          context.hostWindowRequests.agentAvatarChanged(reader.result as string);
          return;
        } catch (e) {
          logger.error({ message: "Failed to load agent avatar", error: e });
        }
      }

      context.hostWindowRequests.agentAvatarChanged(undefined);
    })();
  }, [
    context.state.shared.assignedAgent?.avatarToken,
    context.state.shared.orgConfig?.defaultAgentAvatarToken
  ]);

  const slotsProvider = (token: string, start: number, end: number) =>
    spinnerService.getMeetingSlots(token, start, end);

  return {
    foreCallStore,
    context,
    uiStore,
    state,
    shortOrgConfig,
    slotsProvider,
    shouldShowPoweredBy,
    smallCallingHeader,
    chatComponent,
    breakingPoints,
    currentPage,
    isLoadingFirstTime,
    agents,
    usersTyping,
    dynamicSuggestions,
    onMeetingSlotSelected,
    onChatScrolledToTop,
    scrollChatToBottom,
    onCloseClicked,
    sendMessage,
    deinitWidget,
    phoneRegion,
    maintenanceEnabled,
    maintenanceMessage
  };
};
